import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search } from 'lucide-react';
import Card from '../../components/ui/Card';
import Badge from '../../components/ui/Badge';
import Button from '../../components/ui/Button';
import Table from '../../components/ui/Table';
import ProgressBar from '../../components/ui/ProgressBar';
import { tasks, employees, stores } from '../../data/mockData';

export default function TaskList() {
  const navigate = useNavigate();
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [search, setSearch] = useState('');

  const enriched = tasks.map(t => ({
    ...t,
    assigneeName: employees.find(e => e.id === t.assignedTo)?.name || t.assignedTo,
    storeName: stores.find(s => s.id === t.storeId)?.name || t.storeId,
  }));

  const filtered = enriched.filter(t => {
    if (search && !t.title.toLowerCase().includes(search.toLowerCase())) return false;
    if (filterType && t.type !== filterType) return false;
    if (filterStatus && t.status !== filterStatus) return false;
    if (filterPriority && t.priority !== filterPriority) return false;
    return true;
  });

  const counts = { pending: tasks.filter(t => t.status === 'pending').length, in_progress: tasks.filter(t => t.status === 'in_progress').length, completed: tasks.filter(t => t.status === 'completed').length };

  const columns = [
    { key: 'id', label: 'ID', render: v => <code style={{ fontSize: 11, background: '#F3F4F6', padding: '2px 6px', borderRadius: 4, color: '#6B7280' }}>{v}</code> },
    { key: 'title', label: 'Название', render: v => <span style={{ fontWeight: 600, fontSize: 13 }}>{v}</span> },
    { key: 'type', label: 'Тип', render: v => <Badge variant={v === 'osg' ? 'osg' : 'display'}>{v === 'osg' ? '🛡️ ОСГ' : '📦 Выкладка'}</Badge> },
    { key: 'priority', label: 'Приоритет', render: v => <Badge variant={v === 'urgent' ? 'urgent' : 'normal'}>{v === 'urgent' ? '🔥 Срочный' : 'Обычный'}</Badge> },
    { key: 'status', label: 'Статус', render: v => <Badge variant={v}>{v === 'pending' ? 'Ожидает' : v === 'in_progress' ? 'В работе' : 'Завершено'}</Badge> },
    { key: 'assigneeName', label: 'Назначено', render: v => <span style={{ fontSize: 13 }}>{v}</span> },
    { key: 'storeName', label: 'Магазин', render: v => <span style={{ fontSize: 12, color: '#6B7280' }}>{v}</span> },
    { key: 'itemCount', label: 'Товаров' },
    { key: 'progress', label: 'Прогресс', render: v => <ProgressBar value={v} showLabel /> },
    { key: 'dueDate', label: 'Дедлайн', render: v => (
      <span style={{ fontSize: 12, color: new Date(v) < new Date() ? '#FF3B30' : '#6B7280', fontWeight: new Date(v) < new Date() ? 700 : 400 }}>
        {new Date(v).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
      </span>
    )},
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: '#1A1A2E' }}>Все задачи</h1>
          <p style={{ color: '#8E8E93', fontSize: 13, marginTop: 2 }}>Всего: {tasks.length}</p>
        </div>
        <Button icon={<Plus size={14} />} onClick={() => navigate('/tasks/create')}>Создать задачу</Button>
      </div>

      {/* Quick stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
        {[
          { label: 'Ожидает', count: counts.pending, color: '#8E8E93', bg: '#F3F4F6' },
          { label: 'В работе', count: counts.in_progress, color: '#4A6CF7', bg: '#EEF2FF' },
          { label: 'Завершено', count: counts.completed, color: '#34C759', bg: '#F0FFF4' },
        ].map(s => (
          <div key={s.label} style={{ background: s.bg, borderRadius: 10, padding: '14px 20px', textAlign: 'center' }}>
            <div style={{ fontSize: 28, fontWeight: 800, color: s.color }}>{s.count}</div>
            <div style={{ fontSize: 12, color: '#6B7280' }}>{s.label}</div>
          </div>
        ))}
      </div>

      <Card padding="0">
        <div style={{ padding: '14px 20px', borderBottom: '1px solid #F3F4F6', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#F5F6FA', borderRadius: 8, padding: '7px 12px', flex: 1, minWidth: 200 }}>
            <Search size={14} color="#9CA3AF" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Поиск задач..." style={{ border: 'none', background: 'transparent', outline: 'none', fontSize: 13, width: '100%', fontFamily: 'inherit' }} />
          </div>
          <select value={filterType} onChange={e => setFilterType(e.target.value)} style={{ padding: '7px 12px', border: '1px solid #E5E7EB', borderRadius: 8, fontSize: 13, background: '#fff', cursor: 'pointer', fontFamily: 'inherit' }}>
            <option value="">Все типы</option>
            <option value="osg">ОСГ проверка</option>
            <option value="display">Выкладка</option>
          </select>
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ padding: '7px 12px', border: '1px solid #E5E7EB', borderRadius: 8, fontSize: 13, background: '#fff', cursor: 'pointer', fontFamily: 'inherit' }}>
            <option value="">Все статусы</option>
            <option value="pending">Ожидает</option>
            <option value="in_progress">В работе</option>
            <option value="completed">Завершено</option>
          </select>
          <select value={filterPriority} onChange={e => setFilterPriority(e.target.value)} style={{ padding: '7px 12px', border: '1px solid #E5E7EB', borderRadius: 8, fontSize: 13, background: '#fff', cursor: 'pointer', fontFamily: 'inherit' }}>
            <option value="">Любой приоритет</option>
            <option value="urgent">Срочный</option>
            <option value="normal">Обычный</option>
          </select>
        </div>
        <Table columns={columns} data={filtered} emptyText="Задачи не найдены" />
      </Card>
    </div>
  );
}
